/**
 * 이상한 방 - 체인저 팔레트의 잠금 계산 (전부 `core/verbs`를 실제로 돌려 본 결과다)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.3절(거부는 오류 메시지가 아니라 선택 불가)
 *            `이상한방-상태모델-v0.1.md` 4.2절(무변화 회전 차단) · 5.1절(한 물건 한 번)
 *            로드맵 M4(`changerTool` - "core/verbs로 미리 검사해 팔레트 버튼을 잠근다")
 *
 * `changerTool.ts`에서 갈라져 나온 **순수 함수**들이다. 도구가 상태(초안·선택·대기 요청)를 들고, 여기는 "지금 이 초안에서
 * 이 동사가 되는가"만 답한다. 그 답은 판정이 아니라 잠금이다 - 최종 판정은 호스트(`matchChange.ts`)가 같은 verbs로 다시 한다.
 *
 * ## 이 파일에 규칙의 비교식이 없다 - 사유를 만들지도 않는다
 *
 * 회전 대칭 4면 잠기는 이유는 `applyRotate`가 `isRotationVisible`로 `no-visible-change`를 내기 때문이고, 이미 건드린 물건이
 * 잠기는 이유는 `changedIds`를 받은 verbs가 `already-changed`를 내기 때문이다. `rotSymmetry === 4`나 `touched.includes(id)`를
 * 여기서 적으면 그것이 규칙의 두 번째 사본이다 - `changerTool.test.ts`가 "잠금 = verbs의 답"을 동사마다 대조한다.
 *
 * 같은 이유로 이 파일은 `VerbRejection` 값을 **만들지 않는다.** 물건이 없을 때도 `'unknown-instance'`를 적는 대신 verbs를 한 번
 * 실제로 불러 그 답(동사 불허가 먼저인지, 없는 물건인지는 verbs의 순서다)을 그대로 돌려준다. 사유 문자열이 나오는 자리는 verbs의
 * 답을 **해석**하는 비교식 넷뿐이다 - 프로브 해석 둘(옮김의 `no-visible-change` = "옮길 수 있다" · 생김의 `verb-not-allowed`만 잠금)과
 * 회전의 "다음 회전을 더 본다"(`no-visible-change` · `cell-occupied` · `out-of-bounds`). `changerTool.test.ts`가 소스에서 그 밖의 자리를 잡는다.
 */

import { isFloorPlaceable } from '../catalog/placement'
import { ROT_STEP_COUNT, type CatalogEntry, type InstanceId, type PaletteIndex, type PlayerId, type RoomState, type Rot, type Verb } from '../core/types'
import { applyAdd, applyMove, applyRecolor, applyRemove, applyRotate, type VerbContext, type VerbRejection, type VerbResult } from '../core/verbs'
import { createRng } from '../core/rng'
import type { Catalog } from '../core/room'

/* --- 상수 ------------------------------------------------------------------------ */

/** 새로 놓는 소품의 기본 회전. 돌리는 것은 놓은 뒤의 다른 동사가 아니라(5.1절 한 물건 한 번) 놓기 전에 정해진 값이다. */
export const ADD_ROT: Rot = 0

/** 생김용 카탈로그 스트립의 길이. 다 보여 주면 고르는 데 60초를 쓴다 - 시드가 고른 여덟이면 충분하다. */
export const CHANGER_CATALOG_SIZE = 8

/** 프로브용 셀. 값은 verbs에 닿지 않는다 - 동사 불허·없는 물건·없는 소품이 자리 검사보다 먼저 걸린다(`resolveTarget`·`applyAdd`의 순서). */
const PROBE_CELL = { x: 0, y: 0, z: 0 } as const

/** 카탈로그 스트립 시드 접미사. 방 시드와 갈라 두어 스트립이 바뀌어도 방·변화가 흔들리지 않는다(`rng.ts` 머리말). */
const CATALOG_SEED_SUFFIX = ':changer-catalog'

/* --- 타입 ------------------------------------------------------------------------ */

/** 잠긴 이유. `VerbRejection`은 verbs가 말한 것, 나머지 둘은 verbs가 볼 수 없는 자리(예산 · 고른 물건 없음)다. */
export type ChangerLockReason = VerbRejection | 'budget' | 'no-target'

export type NextRot = Readonly<{ rot: Rot } | { reason: VerbRejection }>
export type NextColor = Readonly<{ colorId: PaletteIndex } | { reason: VerbRejection }>

/* --- 카탈로그 스트립 -------------------------------------------------------------- */

/**
 * 시드가 고른 여덟. id 순으로 세운 뒤 섞으므로 `Map`의 삽입 순서에 기대지 않는다.
 * 놓을 수 있는 소품인가는 생성기와 같은 함수(`catalog/placement.isFloorPlaceable`)가 답한다 - 벽면물·천장물은 빠진다.
 */
export function pickChangerCatalog(catalog: Catalog, seed: string, size = CHANGER_CATALOG_SIZE): readonly CatalogEntry[] {
  const usable = [...catalog.values()].filter(isFloorPlaceable).sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
  return createRng(`${seed}${CATALOG_SEED_SUFFIX}`).shuffle(usable).slice(0, size)
}

/* --- 다음 값 ---------------------------------------------------------------------- */

/** 거부 결과의 사유. `ok`인 결과를 넘기는 것은 계약 위반이다 - "물건이 없는데 verbs가 수용했다"는 있을 수 없는 조합이라 던진다. */
function reasonOf(result: VerbResult): VerbRejection {
  if (result.ok) throw new Error('changerLocks: 거부를 기대한 프로브가 수용됐다')
  return result.reason
}

/**
 * 후보를 순서대로 verbs에 찔러 처음 통과하는 것을 고른다. 거부가 `retryable`이면 다음 후보를 보고, 아니면 그 자리에서
 * 그 사유다(동사 불허·이미 건드림·없는 물건은 값과 무관하니 첫 답이 곧 이유다). 전부 떨어지면 마지막 사유가 남는다.
 * 후보가 비어 있는 것은 호출자의 계약 위반이다(팔레트 0색 · 회전 0단계).
 */
function firstAccepted<T>(
  candidates: readonly T[],
  probe: (candidate: T) => VerbResult,
  retryable: (reason: VerbRejection) => boolean,
): Readonly<{ value: T } | { reason: VerbRejection }> {
  let last: VerbRejection | null = null
  for (const candidate of candidates) {
    const result = probe(candidate)
    if (result.ok) return { value: candidate }
    if (!retryable(result.reason)) return { reason: result.reason }
    last = result.reason
  }
  if (last === null) throw new Error('changerLocks: 후보가 하나도 없다')
  return { reason: last }
}

/** 회전이 자리를 못 잡으면(경계 · 점유) 다음 회전을 본다. 무변화도 다음을 본다 - 대칭 2면 절반이 무변화다. */
function isRotRetryable(reason: VerbRejection): boolean {
  return reason === 'no-visible-change' || reason === 'cell-occupied' || reason === 'out-of-bounds'
}

/**
 * 보이는 다음 회전. 대칭 4면 어느 것도 보이지 않아 `no-visible-change`가 남는다 - 그것이 잠금의 이유가 된다.
 * 물건이 없으면 verbs가 그 이유를 말한다(회전값은 무엇이든 좋다 - 동사 불허와 없는 물건이 값보다 먼저 검사된다).
 */
export function nextVisibleRot(draft: RoomState, id: InstanceId, ctx: VerbContext): NextRot {
  const obj = draft.objects.get(id)
  if (obj === undefined) return { reason: reasonOf(applyRotate(draft, id, ADD_ROT, ctx)) }
  const rots: Rot[] = []
  for (let step = 1; step < ROT_STEP_COUNT; step += 1) rots.push(((obj.rot + step) % ROT_STEP_COUNT) as Rot)
  const picked = firstAccepted(rots, (rot) => applyRotate(draft, id, rot, ctx), isRotRetryable)
  return 'value' in picked ? { rot: picked.value } : picked
}

/** 보이는 다음 색. 현재 색은 `no-visible-change`로 걸리고, 그 다음 색부터 순서대로 본다. 물건이 없으면 verbs의 답이다. */
export function nextVisibleColor(draft: RoomState, id: InstanceId, ctx: VerbContext, paletteSize: number): NextColor {
  const obj = draft.objects.get(id)
  if (obj === undefined) return { reason: reasonOf(applyRecolor(draft, id, 0, ctx)) }
  const colors: PaletteIndex[] = []
  for (let step = 1; step <= paletteSize; step += 1) colors.push((obj.colorId + step) % paletteSize)
  const picked = firstAccepted(colors, (colorId) => applyRecolor(draft, id, colorId, ctx), (reason) => reason === 'no-visible-change')
  return 'value' in picked ? { colorId: picked.value } : picked
}

/* --- 잠금 ------------------------------------------------------------------------ */

/**
 * 생김이 지금 가능한가. 없는 소품으로 찔러 본다 - 동사 불허가 소품 존재보다 먼저 검사되므로(`applyAdd`의 순서)
 * `verb-not-allowed`면 잠금이고 `unknown-catalog`면 열려 있다. `config.allowedVerbs.has('add')`를 여기서 읽지 않는
 * 이유는 그 검사 순서가 verbs의 것이어야 하기 때문이다.
 */
export function addLock(draft: RoomState, me: PlayerId, ctx: VerbContext): ChangerLockReason | null {
  const probe = applyAdd(draft, { id: '', catalogId: '', cell: PROBE_CELL, rot: ADD_ROT, colorId: 0, placedBy: me }, ctx)
  return !probe.ok && probe.reason === 'verb-not-allowed' ? probe.reason : null
}

export type LockInputs = Readonly<{
  draft: RoomState
  ctx: VerbContext
  me: PlayerId
  paletteSize: number
  /** 고른 물건. 없으면 `null`. */
  selectedId: InstanceId | null
  /** 남은 예산(표시용 숫자 - `changerTool` 머리말). 0 이하면 **새** 변화가 잠긴다. */
  budgetLeft: number
  /**
   * 고른 물건이 이미 자기 변화를 갖고 있는가. 그러면 그 물건의 동사는 변화를 **갈아 끼우는** 것이라 예산을 쓰지 않는다
   * (되돌리기가 하나 돌려주고 새 요청이 그 하나를 쓴다 - `changerTool.reedit`). 생김은 새 물건이라 여기 해당하지 않는다.
   */
  reediting: boolean
}>

/** 동사 하나의 잠금. 예산 → 대상 유무 → verbs의 답 순이다. */
export function lockFor(verb: Verb, inputs: LockInputs): ChangerLockReason | null {
  const { draft, ctx, selectedId } = inputs
  // 생김은 언제나 새 물건이라 예산을 쓴다. 나머지 넷은 고른 물건을 갈아 끼우는 중이면 쓰지 않는다.
  if (inputs.budgetLeft <= 0 && (verb === 'add' || !inputs.reediting)) return 'budget'
  if (verb === 'add') return addLock(draft, inputs.me, ctx)
  if (selectedId === null) return 'no-target'
  switch (verb) {
    case 'remove': {
      const result = applyRemove(draft, selectedId, ctx)
      return result.ok ? null : result.reason
    }
    case 'rotate': {
      const next = nextVisibleRot(draft, selectedId, ctx)
      return 'rot' in next ? null : next.reason
    }
    case 'recolor': {
      const next = nextVisibleColor(draft, selectedId, ctx, inputs.paletteSize)
      return 'colorId' in next ? null : next.reason
    }
    case 'move': {
      // 목표 셀은 아직 없다. 제자리로 옮기는 요청을 찔러 보면 동사 불허 → 물건 존재 → 이미 건드림 순으로 걸리고,
      // 전부 통과한 물건만 `no-visible-change`(같은 셀)에 닿는다 - 그것이 "옮길 수 있다"다. 물건이 없으면 셀은 무엇이든
      // 좋다 - verbs가 그 앞에서 답한다(여기서 `unknown-instance`를 적지 않는다).
      const cell = draft.objects.get(selectedId)?.cell ?? PROBE_CELL
      const result = applyMove(draft, selectedId, cell, ctx)
      if (result.ok) return null
      return result.reason === 'no-visible-change' ? null : result.reason
    }
  }
}
